import moment from 'moment';
import uuid from 'uuid/v1';

import {
  getCurrencyValue,
  getPricesCategoryForm,
  getPricesFixedForm
} from 'utils/accommodations-and-activities/prepareAccommodationAndActivityData';

import { getPrices as getActivityPrices } from 'utils/accommodations-and-activities/prepareReceivedActivityData';

import { INTEGRATIONS_NAMES, WEEK_DAYS } from 'constants/base';
import { PRICES_PER_DAY } from '../../pages/create-accommodation/constants';
import { transformReceivedDeactivationDates } from './transformReceivedDeactivationDates';

export const getReceivedAccommodationData = data => {
  const generalForm = getGeneralInfo(data).generalForm;

  const descriptionForm = {
    overview: data.overview,
    additionalInfo: data.additionalInfo,
    included: data.included,
    notIncluded: data.notIncluded,
    cancellation: data.cancellation
  };

  const prices = getPrices(data);

  const addOnsForm = {
    addOns: data.addOn.map(item => ({
      id: parseInt(item.id),
      title: item.title,
      description: item.description,
      coverPhoto: {
        image: item.photo
      },
      USD: getCurrencyValue(item.prices, 'USD'),
      EUR: getCurrencyValue(item.prices, 'EUR'),
      NOK: getCurrencyValue(item.prices, 'NOK'),
      isCreated: true
    }))
  };

  const mandatoryFeesForm = {
    mandatoryFees: data.mandatoryFee.map(item => ({
      id: parseInt(item.id),
      title: item.title,
      description: item.description,
    
      USD: getCurrencyValue(item.prices, 'USD'),
      EUR: getCurrencyValue(item.prices, 'EUR'),
      NOK: getCurrencyValue(item.prices, 'NOK'),
      isCreated: true
    }))
  };

  const relatedActivitiesForm = {
    relatedActivities: data.relatedActivities.map(item => {
      const norwegianFields = {};
      if (item.titleNo) norwegianFields.title = item.titleNo;
      if (item.overviewNo) norwegianFields.overview = item.overviewNo;

      return {
        id: parseInt(item.id),
        title: item.title,
        overview: item.overview,
        Norwegian: norwegianFields,
        photo: item.photo,
        scheduleType: item.scheduleType,
        pricesType: item.pricesType,
        isCreated: true,
        prices: getActivityPrices({
          prices: item.activitypriceSet,
          scheduleType: item.scheduleType
        })
      };
    })
  };

  const integrationForm = {
    integrations: data.platforms.map(item => ({
      id: parseInt(item.id),
      platform: INTEGRATIONS_NAMES[item.platform] || item.platform,
      titleOnPlatform: item.titleOnPlatform,
      isCreated: true
    }))
  };

  const secondLanguageForms = _getSecondLanguagesForm(data, addOnsForm,mandatoryFeesForm);

  return {
    generalForm,
    descriptionForm,
    ...prices,
    addOnsForm,
    mandatoryFeesForm,
    relatedActivitiesForm,
    integrationForm,
    ...secondLanguageForms
  };
};

export const getGeneralInfo = data => {
  return {
    generalForm: {
      id: parseInt(data.id),
      productCode: data.productCode,
      title: data.title,
      coverPhoto: {
        image: data.photo
      },
      guests: parseInt(data.guestsNumber),
      location: data.location,
      availability: {
        startDate: moment(data.fromDate, 'YYYY-MM-DD'),
        endDate:
          !data.toDate || data.toDate === 'None'
            ? null
            : moment(data.toDate, 'YYYY-MM-DD')
      },
      checkInAfter: moment(data.checkInTime, 'HH:mm:ss'),
      checkOutBefore: moment(data.checkOutTime, 'HH:mm:ss'),
      bedDetails: data.bedDetails,
      minNights: data.minNights || 1,
      color : data.color,
      deactivatedDates: transformReceivedDeactivationDates(
        data.accommodationbookingSet
      )
    }
  };
};

const pricesDailyForm = data => {
  const prices = WEEK_DAYS.reduce((acc, curr) => ((acc[curr] = {}), acc), {});

  if (data.prices?.[0]?.day) {
    data.prices.forEach(price => {
      if (prices[price.day]) {
        prices[price.day][price.currency.currency] = price.value;
      } else {
        prices[price.day] = {
          [price.currency.currency]: price.value
        };
      }
    });
  } else {
    return {
      days: WEEK_DAYS.map(day => ({
        id: uuid(),
        day: day,
        ...PRICES_PER_DAY
      }))
    };
  }

  return {
    days: Object.keys(prices).map(price => {
      return {
        id: uuid(),
        day: price,
        USD: `${prices[price]['USD']}` || '0',
        EUR: `${prices[price]['EUR']}` || '0',
        NOK: `${prices[price]['NOK']}` || '0'
      };
    })
  };
};

export const getPrices = data => {
  return {
    pricesFixedForm: getPricesFixedForm(data),
    pricesCategoryForm: getPricesCategoryForm(data),
    pricesDailyForm: pricesDailyForm(data)
  };
};

const _getSecondLanguagesForm = (data, addOnsForm,mandatoryFeesForm) => {
  const secondLanguage = 'Norwegian';
  const secondLanguageForms = {
    [`generalForm-${secondLanguage}`]: {},
    [`descriptionForm-${secondLanguage}`]: {}
  };

  if (data.titleNo) {
    secondLanguageForms[`generalForm-${secondLanguage}`] = {
      title: data.titleNo,
      bedDetails: data.bedDetailsNo
    };

    secondLanguageForms[`descriptionForm-${secondLanguage}`] = {
      overview: data.overviewNo,
      additionalInfo: data.additionalInfoNo,
      included: data.includedNo,
      notIncluded: data.notIncludedNo,
      cancellation: data.cancellationNo,
    };

    addOnsForm.addOns = addOnsForm.addOns.map((item, index) => ({
      ...item,
      [secondLanguage]: {
        id: parseInt(item.id),
        title: data.addOn[index]['titleNo'],
        description: data.addOn[index]['descriptionNo']
      }
    }));

    mandatoryFeesForm.mandatoryFees = mandatoryFeesForm.mandatoryFees.map((item, index) => ({
      ...item,
      [secondLanguage]: {
        id: parseInt(item.id),
        title: data.mandatoryFee[index]['titleNo'],
        description: data.mandatoryFee[index]['descriptionNo']
      }
    }));
  }

  return secondLanguageForms;
};
